/**
 * THE JOB SHEET. What a cleaner reads at the door, and the two buttons.
 *
 * `docs/architecture.md` section 6 names this as one of the three screens that decide whether the
 * system works, and it sets the order of the page: the do-not list above the fold, then the address
 * with one-tap directions, the window, the written scope, pets, how to get in, what the client
 * asked for last time, and the codes for that home. Right under the do-not list, the three things the
 * household checks first (ops #86), as the home said them when the sheet was frozen. Start and finish are two buttons.
 *
 * IT IS ONE PHONE SCREEN, so the facts are drawn as small as they can be read and no smaller: the
 * address and its Directions button share a row, the four things she checks at the door share a
 * two-column grid, and everything that is a decision rather than a fact is a 44px row she opens.
 * The written scope is behind one of those rows because it is the same paragraph every week, and
 * the do-not list, which is different every week, is the panel she cannot scroll past.
 *
 * The sheet is FROZEN the first time anybody opens it, on the server, and this screen only reads
 * it back. Nothing here is edited after the fact, and there is no price on it by schema invariant.
 *
 * CODES ARE READ LIVE, NEVER COPIED ONTO THE SHEET. The sheet lists which codes exist, by label,
 * so she knows to ask for the lockbox rather than the front door; each value is a separate audited
 * read that the server allows a cleaner from twelve hours before the window to four hours after
 * it. Outside that, a cleaner's screen says so in one sentence and offers nothing to press. The
 * office is under no window, so for her the list is drawn whenever the server returns it, and the
 * server, with its recent-sign-in check, still decides every reveal.
 *
 * NO POSITION, NO PHOTO, NO DEVICE ID. Start and finish stamp a time and nothing else. The brief
 * names surveillance as a thing this must not become, and CLAUDE.md binding rule 4 says how that is
 * written in a design: the columns do not exist, so there is nothing here to send.
 *
 * Both roles open the same route. The server decides what each gets back, and refuses a cleaner a
 * visit that is not hers with a 404 she cannot tell from a missing row.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ISSUE_KINDS,
  VISIT_KIND_LABELS,
  humanise,
  type ClientLanguage,
  type IssueKind,
} from '@fk/shared';
import {
  ApiError,
  finishVisit,
  getReport,
  getSheet,
  raiseIssue,
  reportNoAccess,
  saveReport,
  startVisit,
  submitReport,
  writeReportForClient,
  type Me,
  type ReportRow,
  type SheetChecklist,
  type SheetView,
} from '../api.ts';
import { Card, Detail, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { ChecksFirstPanel, DoNotPanel } from '../components/DoNotPanel.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import { Busy, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { RevealCode } from '../components/RevealCode.tsx';
import { VisitStatusPill } from '../components/StatusPills.tsx';
import { CheckField } from '../components/VisitBits.tsx';
import { WordsCheck, refusedFieldsOf, useRefusedBoxes } from '../components/WordsCheck.tsx';
import {
  dayOf,
  directionsUrl,
  formatInstant,
  formatWindow,
  longDay,
  mediumDay,
  orNull,
} from '../format.ts';
import { ACCESS_SECRET_KIND_LABELS, CLIENT_LANGUAGE_LABELS, ISSUE_KIND_LABELS } from '../labels.ts';

/**
 * The two rows the bottom of this screen is built from. The quiet one is a thing she can open and
 * the still one is a thing that is not ready yet, and both are 44px whatever is written in them.
 */
const OPENABLE_ROW =
  'flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-navy/25 bg-cream px-4 font-serif text-lg';
const WAITING_ROW =
  'flex min-h-11 items-center gap-2 rounded-xl border border-navy/12 bg-parchment px-4';

/**
 * THE HOME, IN THE HOUSEHOLD'S WORDS (ops #74 item 6), where the cleaner app draws it: the version of
 * their plan this sheet froze at its first open, read only and as they gave it, never translated or
 * tidied, under what they check first; and under it the keys held for this home, a cleaner's own, or
 * for the office each with who holds it. Nothing is drawn of either that the sheet does not carry, and
 * a Worker before it answers neither key.
 */
function HomeInTheirWords({
  plan,
  keys,
}: {
  plan: NonNullable<SheetView['homePlan']> | null;
  keys: NonNullable<SheetView['keysHeld']>;
}) {
  return (
    <>
      {plan ? (
        <div
          data-home-plan
          className="mt-2 rounded-lg border border-l-4 border-navy/15 border-l-navy/40 bg-parchment px-3 py-2"
        >
          <p className="font-serif text-sm tracking-wide uppercase">
            The home, in the household&apos;s words
          </p>
          <p className="mt-1 text-sm whitespace-pre-line">{plan.body}</p>
        </div>
      ) : null}
      {keys.length > 0 ? (
        <div
          data-keys-held
          className="mt-2 rounded-lg border border-navy/15 bg-parchment px-3 py-2"
        >
          <p className="font-serif text-sm tracking-wide uppercase">Keys held for this home</p>
          <ul className="mt-1 flex flex-col gap-0.5 text-sm">
            {keys.map((key) => {
              const since = dayOf(key.since);
              return (
                <li key={`${key.cleanerId ?? ''}${key.keyKind}${key.keyLabel}`}>
                  {humanise(key.keyKind)}: {key.keyLabel}
                  {key.displayName ? `, held by ${key.displayName}` : ''}
                  {since ? `, since ${mediumDay(since)}` : ''}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </>
  );
}

/** The chevron on an openable row, turned over while it is open. */
function RowChevron() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-[18px] w-[18px] shrink-0 group-open:rotate-180"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 8l5 5 5-5" />
    </svg>
  );
}

export function JobSheet({ me }: { me: Me }) {
  const { id = '' } = useParams();
  const [view, setView] = useState<SheetView | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState<string | null>(null);
  const [ticks, setTicks] = useState<readonly string[] | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const returnPath = `/visits/${id}/sheet`;

  useEffect(() => {
    let live = true;
    setError(null);
    getSheet(id)
      .then((answer) => {
        if (live) setView(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The job sheet did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  // The company checklist's ticks (ops #87): what the server holds on the first read, then what she
  // ticks, which a reload after a finish or a save leaves alone, until the submit fixes them.
  useEffect(() => {
    const list = view?.checklist;
    if (!list) return;
    setTicks((now) => (now === null || list.ticksFixed ? list.ticks : now));
  }, [view]);

  const toggle = useCallback((tick: string) => {
    setTicks((now) => {
      const set = new Set(now ?? []);
      if (set.has(tick)) set.delete(tick);
      else set.add(tick);
      return [...set].sort();
    });
  }, []);

  const back = (
    <Link
      to={`/visits/${id}`}
      aria-label="Back to the visit"
      className="-ml-3.5 mb-1 inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-navy/60"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 5l-5 5 5 5" />
      </svg>
    </Link>
  );

  if (error) {
    return (
      <div>
        {back}
        <ErrorNotice title="The job sheet did not load" message={error.message} onRetry={reload} />
      </div>
    );
  }

  if (!view) {
    return (
      <div>
        {back}
        <Busy what="the job sheet" />
      </div>
    );
  }

  const { visit, sheet, scope, crew, secrets, windowOpen } = view;
  const others = crew.filter((person) => person.cleanerId !== me.cleanerId);
  const window = formatWindow(sheet.windowStart, sheet.windowEnd);

  return (
    <div>
      {back}
      <ScreenTitle
        title={window ?? 'No arrival window set'}
        subtitle={longDay(visit.scheduledDate)}
      />
      <div className="mb-2 flex flex-wrap gap-2">
        <VisitStatusPill status={visit.status} />
        <Pill>{VISIT_KIND_LABELS[visit.kind]}</Pill>
      </div>

      <DoNotPanel text={sheet.doNotList} />
      <ChecksFirstPanel text={sheet.checksFirst ?? null} />
      <HomeInTheirWords plan={view.homePlan ?? null} keys={view.keysHeld ?? []} />
      {view.checklist ? (
        <ChecklistPanel
          checklist={view.checklist}
          crew={crew}
          ticks={ticks ?? view.checklist.ticks}
          onToggle={toggle}
        />
      ) : null}

      {/* px/py rather than p: Tailwind emits the p-* block first, so a p-3.5 here loses to Card's p-4. */}
      <Card className="mt-2 px-3.5 py-3.5">
        <div className="flex items-center gap-3">
          <div className="flex min-w-0 grow flex-col gap-0.5">
            <span className="text-xs tracking-wide text-navy/55 uppercase">Address</span>
            <span className="text-base leading-snug whitespace-pre-line">{sheet.addressText}</span>
          </div>
          <a
            href={directionsUrl(sheet.addressText)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 shrink-0 items-center rounded-lg border border-navy/25 bg-cream px-4 text-base font-medium whitespace-nowrap"
          >
            Directions
          </a>
        </div>
        <dl className="mt-2 grid grid-cols-2 gap-x-3">
          <Detail label="Pets" value={sheet.pets} />
          <Detail label="Getting in" value={sheet.entryInstructions} />
          <Detail label="The client asked for" value={sheet.clientAskedFor} />
          <Detail
            label="What is included"
            value={
              scope ? (
                `${VISIT_KIND_LABELS[visit.kind]}, scope ${scope.version}`
              ) : (
                <span className="text-coral">
                  The written scope this sheet was frozen against could not be read back.
                </span>
              )
            }
          />
        </dl>
      </Card>

      {scope ? (
        <details className="group mt-2 rounded-xl border border-navy/12 bg-parchment">
          <summary className="flex min-h-11 cursor-pointer items-center gap-2 px-4 font-serif text-base">
            <span className="grow">The written scope</span>
            <RowChevron />
          </summary>
          <p className="px-4 pb-3 text-sm whitespace-pre-line">{scope.body}</p>
        </details>
      ) : null}

      <Card className="mt-2 py-3">
        <h2 className="font-serif text-lg leading-6">Who is on it</h2>
        {me.role === 'cleaner' ? (
          <p className="mt-0.5 text-sm">
            {others.length === 0
              ? 'Just you on this one.'
              : `With ${others.map((person) => person.displayName).join(' and ')}.`}
          </p>
        ) : crew.length === 0 ? (
          <p className="mt-0.5 text-sm text-coral">Nobody assigned.</p>
        ) : (
          <p className="mt-0.5 text-sm">{crew.map((person) => person.displayName).join(' and ')}</p>
        )}
      </Card>

      <Card className="mt-2 px-3 py-2.5">
        <h2 className="font-serif text-lg leading-6">Codes</h2>
        {me.role === 'cleaner' ? (
          <p className="mt-1 text-xs text-navy/75">
            Codes open twelve hours before the visit and close four hours after it. Nothing can be
            shown before then.
          </p>
        ) : null}
        {me.role === 'cleaner' && !windowOpen ? null : secrets.length === 0 ? (
          <p className="mt-1.5 text-sm text-navy/75">
            No codes are on file for this home. If a door needs one, the office has not been told.
          </p>
        ) : (
          <ul className="mt-1.5 flex flex-col gap-2">
            {secrets.map((secret) => (
              <li key={secret.id} className="flex flex-wrap items-center gap-x-3">
                <div className="min-w-0 grow">
                  <p className="text-sm font-medium">
                    {secret.label ?? ACCESS_SECRET_KIND_LABELS[secret.kind]}
                  </p>
                  {secret.label ? (
                    <p className="text-xs text-navy/60">{ACCESS_SECRET_KIND_LABELS[secret.kind]}</p>
                  ) : null}
                </div>
                <div className="max-w-full min-w-0 shrink-0">
                  <RevealCode secretId={secret.id} returnPath={returnPath} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {said ? (
        <div className="mt-2">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      <TheTwoButtons
        key={visit.status}
        visitId={visit.id}
        status={visit.status}
        onDone={(sentence) => {
          setSaid(sentence);
          reload();
        }}
      />

      {/*
       * The waiting row is drawn only where finishing is still reachable. The server refuses a start
       * from anything but planned or confirmed and a finish from anything but in progress, so on a
       * cancelled visit or one she could not get into there is no path to done, and a row saying
       * "once you finish" would be the screen promising what the server has already closed.
       */}
      {visit.status === 'done' ? (
        <ReportForm
          visitId={visit.id}
          office={me.role !== 'cleaner'}
          checklist={
            view.checklist && !view.checklist.ticksFixed
              ? { version: view.checklist.version, ticks: ticks ?? view.checklist.ticks }
              : null
          }
          onSubmitted={reload}
        />
      ) : CAN_START.has(visit.status) || visit.status === 'in_progress' ? (
        <div className={`mt-1.5 ${WAITING_ROW}`}>
          <span className="grow font-serif text-base">The report</span>
          <span className="text-xs text-navy/60">Once you finish</span>
        </div>
      ) : null}

      {visit.startedAt || visit.finishedAt ? (
        <dl className="mt-2 flex gap-6">
          <Detail label="Started" value={formatInstant(visit.startedAt)} />
          <Detail label="Finished" value={formatInstant(visit.finishedAt)} />
        </dl>
      ) : null}

      <IssueForm visitId={visit.id} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The company checklist (ops #87).
 * ------------------------------------------------------------------ */

/**
 * THE COMPANY CHECKLIST, the business's paper lists of what is clean when the cleaner leaves, drawn
 * only where the server sends them: to a cleaner it applies to on this visit, and to the office with
 * whom it applies to. Behind one openable row, because it is long and the same most weeks, and the
 * do-not list above it is the panel she cannot scroll past. A list and never a score: nothing here
 * counts what is done.
 */
export function ChecklistPanel({
  checklist,
  crew,
  ticks,
  onToggle,
}: {
  checklist: SheetChecklist;
  crew: readonly { readonly cleanerId: string; readonly displayName: string }[];
  ticks: readonly string[];
  onToggle: (tick: string) => void;
}) {
  const whom = (checklist.appliesTo ?? [])
    .map((id) => crew.find((person) => person.cleanerId === id)?.displayName)
    .filter((name): name is string => Boolean(name));
  const fixed = checklist.ticksFixed;
  const listed = new Set(checklist.sections.flatMap((section) => section.items.map((i) => i.id)));
  const earlier = ticks.filter((tick) => !listed.has(tick));
  return (
    <details className="group mt-2 rounded-xl border border-navy/12 bg-parchment" data-checklist>
      <summary className="flex min-h-11 cursor-pointer items-center gap-2 px-4 font-serif text-base">
        <span className="grow">The company checklist</span>
        <RowChevron />
      </summary>
      <div className="px-4 pb-3">
        {whom.length > 0 ? (
          <p className="text-sm">The checklist applies to {whom.join(' and ')}.</p>
        ) : null}
        <p className="text-xs text-navy/70">
          {fixed
            ? 'What is clean when you leave, as the submitted report has it. The marks stand.'
            : 'Tick what is clean when you leave. Saved with the report, never on its own, and nothing waits on it.'}
        </p>
        {checklist.sections.map((section) => (
          <section key={section.id} className="mt-2">
            <h3 className="text-xs font-semibold tracking-wide uppercase">{section.title}</h3>
            <ul className="mt-0.5 text-sm">
              {section.items.map((item) => (
                <li key={item.id}>
                  <label className="flex min-h-11 items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5 shrink-0"
                      data-tick={item.id}
                      checked={ticks.includes(item.id)}
                      disabled={fixed}
                      onChange={() => onToggle(item.id)}
                    />
                    <span>{item.words}</span>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {earlier.length > 0 ? (
          <p className="mt-2 text-xs">Ticked on an earlier list: {earlier.join(', ')}</p>
        ) : null}
        <p className="mt-2 text-xs text-navy/70">{checklist.never}</p>
      </div>
    </details>
  );
}

/* ------------------------------------------------------------------ *
 * Start, finish, and the door that would not open.
 * ------------------------------------------------------------------ */

const CAN_START = new Set(['planned', 'confirmed']);
const CAN_REPORT_NO_ACCESS = new Set(['planned', 'confirmed', 'in_progress']);

function TheTwoButtons({
  visitId,
  status,
  onDone,
}: {
  visitId: string;
  status: string;
  onDone: (sentence: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState('');

  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onDone(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  const big =
    'flex min-h-[52px] w-full items-center justify-center rounded-lg border px-5 text-lg font-semibold disabled:opacity-50';

  const canStart = CAN_START.has(status);
  const started = status === 'in_progress';

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {problem ? <ErrorNotice title="Not recorded" message={problem} /> : null}

      {canStart ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => startVisit(visitId), 'Started. The time is on the record.')}
          className={`${big} border-navy bg-navy text-cream`}
        >
          {busy ? 'Recording' : 'Start'}
        </button>
      ) : null}

      {/* Drawn but refused before she starts, so the order of the two is never a question. */}
      {canStart || started ? (
        <button
          type="button"
          disabled={busy || !started}
          onClick={() =>
            run(() => finishVisit(visitId), 'Finished. Write the report below while it is fresh.')
          }
          className={`${big} border-navy bg-navy text-cream`}
        >
          {busy && started ? 'Recording' : 'Finish'}
        </button>
      ) : null}

      {CAN_REPORT_NO_ACCESS.has(status) ? (
        <details className="group">
          <summary className={OPENABLE_ROW}>
            <span className="grow">Could not get in</span>
            <RowChevron />
          </summary>
          <div className="mt-1.5 rounded-xl border border-coral/40 bg-parchment p-4">
            <p className="mb-2 text-sm text-navy/75">
              Every owner is told the moment you press this. Say what you found, in a line.
            </p>
            <TextAreaField
              id="no-access-note"
              label="What happened at the door"
              value={note}
              onChange={setNote}
              rows={2}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                run(
                  () => reportNoAccess(visitId, orNull(note)),
                  'Recorded. The owners have been told and will ring you.',
                )
              }
              className={`${big} border-coral bg-coral/15 text-navy`}
            >
              {busy ? 'Recording' : 'I could not get in'}
            </button>
          </div>
        </details>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The report. Saved as a draft, then submitted once.
 * ------------------------------------------------------------------ */

/**
 * Saved as a draft, then submitted once. WHAT WAS DONE AND WHAT SHE ASKED FOR ARE THE HOUSEHOLD'S
 * JOURNAL AT HOME (ops #68), so each box shows, as she types, the coral and amber lines the composer
 * shows, and a submission the server refuses for its words is said under the box it came from.
 * Submit saves the draft first, so a refused submission keeps her words on the server as well as in
 * the boxes, and nothing else about the visit waits on them.
 */
export function ReportForm({
  visitId,
  checklist = null,
  office = false,
  onSubmitted,
}: {
  visitId: string;
  /** The company checklist's ticks to save with the words, where the sheet carries it (ops #87). */
  checklist?: { readonly version: string; readonly ticks: readonly string[] } | null;
  /**
   * The reader is the office, who writes what the client reads of a submitted report (ops #98). The
   * server decides it all the same: a cleaner's answer carries none of it and the door refuses them.
   */
  office?: boolean;
  onSubmitted?: () => void;
}) {
  const ticked = checklist
    ? { checklistVersion: checklist.version, checklistTicks: [...checklist.ticks] }
    : {};
  const [report, setReport] = useState<ReportRow | null | undefined>(undefined);
  const [householdLanguage, setHouseholdLanguage] = useState<ClientLanguage | null>(null);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [whatWasDone, setWhatWasDone] = useState('');
  const [clientAskedFor, setClientAskedFor] = useState('');
  const [notesForOwner, setNotesForOwner] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const boxes = useRefusedBoxes();

  useEffect(() => {
    let live = true;
    setLoadProblem(null);
    getReport(visitId)
      .then((answer) => {
        if (!live) return;
        setReport(answer.report);
        setHouseholdLanguage(answer.householdLanguage ?? null);
        if (answer.report) {
          setWhatWasDone(answer.report.whatWasDone);
          setClientAskedFor(answer.report.clientAskedFor ?? '');
          setNotesForOwner(answer.report.notesForOwner ?? '');
        }
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setLoadProblem(thrown instanceof ApiError ? thrown.message : 'The report did not load.');
      });
    return () => {
      live = false;
    };
  }, [visitId, attempt]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setSaved(null);
    if (!whatWasDone.trim()) {
      setProblem('Say what was done, in a sentence or two.');
      return;
    }
    setBusy(true);
    try {
      await saveReport(visitId, {
        whatWasDone: whatWasDone.trim(),
        clientAskedFor: orNull(clientAskedFor),
        notesForOwner: orNull(notesForOwner),
        ...ticked,
      });
      setSaved('Saved. Submit it when you are done, and it goes on the record.');
      setAttempt((n) => n + 1);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'The report was not saved.');
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setProblem(null);
    setSaved(null);
    boxes.setRefused({});
    setBusy(true);
    try {
      if (whatWasDone.trim()) {
        // The ticks go with the words here too: a Submit pressed with no Save first would otherwise
        // fix the report with none, and nothing after a submit may add them (the skeptic, ops #87).
        await saveReport(visitId, {
          whatWasDone: whatWasDone.trim(),
          clientAskedFor: orNull(clientAskedFor),
          notesForOwner: orNull(notesForOwner),
          ...ticked,
        });
      }
      await submitReport(visitId);
      // Nothing of it reaches the client until a person in the office has read it and written what
      // the client reads (ops #98, the business's rule 6).
      setSaved('Submitted. The office reads it and writes what the client sees.');
      setAttempt((n) => n + 1);
      onSubmitted?.();
    } catch (thrown) {
      const refused = thrown instanceof ApiError ? refusedFieldsOf(thrown.body) : {};
      if (Object.keys(refused).length > 0) boxes.setRefused(refused);
      else {
        setProblem(thrown instanceof ApiError ? thrown.message : 'The report was not submitted.');
      }
    } finally {
      setBusy(false);
    }
  }

  /** The line above what the check found in a box: the draft is safe, and what to do. */
  const refusedTitle =
    'Not submitted. The report is kept as a draft, with your words in it. Change the words marked here and submit it again.';

  if (loadProblem) {
    return (
      <Card className="mt-2">
        <ErrorNotice
          title="The report did not load"
          message={loadProblem}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </Card>
    );
  }

  if (report === undefined) {
    return (
      <Card className="mt-2">
        <Busy what="the report" />
      </Card>
    );
  }

  if (report && report.submittedAt) {
    return (
      <>
        <Card className="mt-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-serif text-lg">The report</h2>
            <Pill tone="sage">Submitted {formatInstant(report.submittedAt)}</Pill>
          </div>
          <dl className="mt-2">
            <Detail label="What was done" value={report.whatWasDone} />
            <Detail label="The client asked for" value={report.clientAskedFor} />
            <Detail label="For the office only" value={report.notesForOwner} />
          </dl>
          <p className="mt-2 text-xs text-navy/60">
            What was written stands. If something was missed, record an issue below.
          </p>
        </Card>
        {office && householdLanguage ? (
          <ForClientPanel
            visitId={visitId}
            report={report}
            householdLanguage={householdLanguage}
            onSaved={() => setAttempt((n) => n + 1)}
          />
        ) : null}
      </>
    );
  }

  return (
    <Card className="mt-2">
      <h2 className="font-serif text-lg">The report</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        A few lines while it is fresh. The client may be sent the first two; the third they are not.
      </p>
      <form onSubmit={save} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        {saved ? (
          <div className="mb-3">
            <SavedNotice>{saved}</SavedNotice>
          </div>
        ) : null}
        <TextAreaField
          id="report-done"
          label="What was done"
          value={whatWasDone}
          onChange={(value) => {
            setWhatWasDone(value);
            boxes.clear('whatWasDone');
          }}
          rows={4}
          hint="Required. In your words, the way you would tell them at the door."
        />
        <WordsCheck
          words={whatWasDone}
          refused={boxes.refused.whatWasDone}
          refusedTitle={refusedTitle}
        />
        <TextAreaField
          id="report-asked"
          label="What the client asked for"
          value={clientAskedFor}
          onChange={(value) => {
            setClientAskedFor(value);
            boxes.clear('clientAskedFor');
          }}
          rows={2}
          hint="Carried onto the next job sheet, so it survives a change of cleaner."
        />
        <WordsCheck
          words={clientAskedFor}
          refused={boxes.refused.clientAskedFor}
          refusedTitle={refusedTitle}
        />
        <TextAreaField
          id="report-office"
          label="A note for the office"
          value={notesForOwner}
          onChange={setNotesForOwner}
          rows={2}
          hint="Not shown to the client. Anything you would rather say to the office than to them."
        />
        <div className="grid grid-cols-1 gap-2">
          <Button type="submit" tone="quiet" full disabled={busy}>
            {busy ? 'Saving' : 'Save the draft'}
          </Button>
          <Button onClick={submit} full disabled={busy}>
            {busy ? 'Saving' : 'Submit the report'}
          </Button>
        </div>
        <p className="mt-2 text-xs text-navy/60">
          Submitting is one way. After it the report is read and never edited.
        </p>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * What the client reads (ops #98).
 * ------------------------------------------------------------------ */

/**
 * WHAT THE CLIENT READS OF A SUBMITTED REPORT, written by the office (ops #98, business #49,
 * `docs/plan-client-language.md` section 4). The business's rule 6: nothing a cleaner writes reaches a
 * client until a person in the office has read it and put it into the client's language in their own
 * words. So the office reads the cleaner's words, shown beside each box, and types the client's
 * version in the household's language, named here; the boxes start empty, or holding the version
 * already written, and nothing is filled in on the office's behalf, an English household's as much as
 * a Spanish one's. The ask's box is drawn only where the cleaner wrote an ask, since the server refuses
 * a version that invents one or drops one. Each box shows, as the office types, the lines the server's
 * claims check would refuse, and a refusal is said under the box it came from.
 */
function ForClientPanel({
  visitId,
  report,
  householdLanguage,
  onSaved,
}: {
  visitId: string;
  report: ReportRow;
  householdLanguage: ClientLanguage;
  onSaved: () => void;
}) {
  const [whatWasDone, setWhatWasDone] = useState(report.forClientWhatWasDone ?? '');
  const [askedFor, setAskedFor] = useState(report.forClientAskedFor ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxes = useRefusedBoxes();
  const written = Boolean(report.forClientAt);
  const stale =
    written && report.forClientLanguage && report.forClientLanguage !== householdLanguage
      ? report.forClientLanguage
      : null;
  const asks = Boolean(report.clientAskedFor);

  async function save(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setSaved(false);
    boxes.setRefused({});
    if (!whatWasDone.trim()) {
      setProblem('Write what was done, as the client will read it.');
      return;
    }
    setBusy(true);
    try {
      await writeReportForClient(visitId, {
        whatWasDone: whatWasDone.trim(),
        askedFor: asks ? orNull(askedFor) : null,
      });
      setSaved(true);
      onSaved();
    } catch (thrown) {
      const refused = thrown instanceof ApiError ? refusedFieldsOf(thrown.body) : {};
      if (Object.keys(refused).length > 0) boxes.setRefused(refused);
      else setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setBusy(false);
    }
  }

  const refusedTitle =
    "Not saved. The cleaner's words stand as they were. Change the words marked here and save it again.";

  return (
    <div data-for-client>
      <Card className="mt-2" label="What the client reads">
        <h2 className="font-serif text-lg">What the client reads</h2>
        <p className="mt-1 text-sm text-navy/70">
          This household reads {CLIENT_LANGUAGE_LABELS[householdLanguage]}. Read the cleaner&apos;s
          words and write them for the client in your own; nothing of the report reaches the client
          until it is saved here.
        </p>
        {written ? (
          <p className="mt-1 text-xs text-navy/60">
            Written {formatInstant(report.forClientAt ?? null)}
            {report.forClientLanguage
              ? ` in ${CLIENT_LANGUAGE_LABELS[report.forClientLanguage]}`
              : ''}
            .
          </p>
        ) : (
          <p className="mt-1 text-xs text-coral">No words for the client yet.</p>
        )}
        {stale ? (
          <p className="mt-1 text-sm text-coral">
            Written in {CLIENT_LANGUAGE_LABELS[stale]}; this household reads{' '}
            {CLIENT_LANGUAGE_LABELS[householdLanguage]} now.
          </p>
        ) : null}
        <form className="mt-3" onSubmit={save} noValidate>
          {problem ? (
            <div className="mb-3">
              <ErrorNotice title="Not saved" message={problem} />
            </div>
          ) : null}
          {saved ? (
            <div className="mb-3">
              <SavedNotice>
                Saved. The client reads these words at home, in place of the cleaner&apos;s.
              </SavedNotice>
            </div>
          ) : null}
          <p className="mb-1 text-xs text-navy/60">The cleaner wrote: {report.whatWasDone}</p>
          <TextAreaField
            id="for-client-done"
            label="What was done, for the client"
            value={whatWasDone}
            onChange={(value) => {
              setWhatWasDone(value);
              boxes.clear('whatWasDone');
            }}
            rows={4}
          />
          <WordsCheck
            words={whatWasDone}
            refused={boxes.refused.whatWasDone}
            refusedTitle={refusedTitle}
          />
          {asks ? (
            <>
              <p className="mb-1 text-xs text-navy/60">
                The cleaner wrote: {report.clientAskedFor}
              </p>
              <TextAreaField
                id="for-client-asked"
                label="What they asked for, for the client"
                value={askedFor}
                onChange={(value) => {
                  setAskedFor(value);
                  boxes.clear('askedFor');
                }}
                rows={2}
              />
              <WordsCheck
                words={askedFor}
                refused={boxes.refused.askedFor}
                refusedTitle={refusedTitle}
              />
            </>
          ) : null}
          <Button type="submit" full disabled={busy}>
            {busy ? 'Saving' : 'Save what the client reads'}
          </Button>
        </form>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Something went wrong.
 * ------------------------------------------------------------------ */

/**
 * WHAT WENT WRONG IS ON THE JOURNAL AT HOME once the office has read it (ops #68), so its box shows
 * the coral and amber lines as she types, and a refusal for its words is said under it, with every
 * word she typed left where it was. A claim or a cut is coral there and a figure is not: what a broken
 * thing was worth is recorded, as the server records it.
 */
export function IssueForm({ visitId }: { visitId: string }) {
  const [kind, setKind] = useState<IssueKind | ''>('');
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');
  const [discoveredByClient, setDiscoveredByClient] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxes = useRefusedBoxes();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setSaved(false);
    boxes.setRefused({});
    if (!kind) {
      setProblem('Say what kind of thing went wrong.');
      return;
    }
    if (!summary.trim()) {
      setProblem('Say what went wrong, in a sentence.');
      return;
    }
    setBusy(true);
    try {
      await raiseIssue(visitId, {
        kind,
        summary: summary.trim(),
        detail: orNull(detail),
        discoveredByClient,
      });
      setKind('');
      setSummary('');
      setDetail('');
      setDiscoveredByClient(false);
      setSaved(true);
    } catch (thrown) {
      const refused = thrown instanceof ApiError ? refusedFieldsOf(thrown.body) : {};
      if (Object.keys(refused).length > 0) boxes.setRefused(refused);
      else setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="group mt-1.5">
      <summary className={OPENABLE_ROW}>
        <span className="grow">Something went wrong</span>
        <RowChevron />
      </summary>
      <div className="mt-1.5 rounded-xl border border-navy/12 bg-parchment p-4">
        <p className="text-sm text-navy/70">
          Damage, a breakage, a missed area, anything. The damage a business survives is the damage
          it finds and reports itself, so say it here first.
        </p>
        <form className="mt-3" onSubmit={submit} noValidate>
          {problem ? (
            <div className="mb-3">
              <ErrorNotice title="Not recorded" message={problem} />
            </div>
          ) : null}
          {saved ? (
            <div className="mb-3">
              <SavedNotice>
                Recorded. The office has it, and the owners are told about damage.
              </SavedNotice>
            </div>
          ) : null}
          <SelectField
            id="issue-kind"
            label="What kind of thing"
            required
            value={kind}
            onChange={setKind}
            options={ISSUE_KINDS}
            toLabel={(value) => ISSUE_KIND_LABELS[value]}
            placeholder="Choose one"
          />
          <TextField
            id="issue-summary"
            label="What went wrong"
            required
            value={summary}
            onChange={(value) => {
              setSummary(value);
              boxes.clear('summary');
            }}
            autoComplete="off"
          />
          <WordsCheck
            words={summary}
            refused={boxes.refused.summary}
            refusedTitle="Not recorded. Change the words marked here and record it again."
            figuresPass
          />
          <TextAreaField
            id="issue-detail"
            label="More detail"
            value={detail}
            onChange={setDetail}
            rows={3}
          />
          <CheckField
            id="issue-client-first"
            label="The client found it first"
            checked={discoveredByClient}
            onChange={setDiscoveredByClient}
            hint="Honest either way. It only changes what we say to them, never whether."
          />
          <Button type="submit" full disabled={busy}>
            {busy ? 'Recording' : 'Record it'}
          </Button>
        </form>
      </div>
    </details>
  );
}
